import { AfterViewInit, ChangeDetectionStrategy, Component, OnInit, inject } from '@angular/core';
import { isDefined } from '@library/utils/helpers/store/is-defined';
import { LangPipe } from '@library/utils/pipes/lang.pipe';
import { LocalStorageService } from '@library/utils/services/local-storage.service';
import { LangsResolver } from '@store/langs/langs.resolver';
import { WeathermapFacade } from '@store/weathermap.facade';
import * as maplibregl from 'maplibre-gl';
import { BehaviorSubject, take } from 'rxjs';
import { BannerComponent } from 'src/app/banner/banner.component';
import { ControlsComponent } from 'src/app/controls/controls.component';
import { FavoriteLayerComponent } from 'src/app/layers/favorite-layer/favorite-layer.component';
import { HomesLayerComponent } from 'src/app/layers/homes-layer/homes-layer.component';
import { LegendComponent } from 'src/app/legend/legend.component';
import { SearchComponent } from 'src/app/search/search.component';
import { CacheService } from 'src/app/services/cache.service';
import { EventManagerService, EventSource } from 'src/app/services/event-manager.service';
import { LayersService } from 'src/app/services/layers.service';
import { LoginService } from 'src/app/services/login.service';
import { PinService } from 'src/app/services/pin.service';
import { SelectedStationService } from 'src/app/services/selected-station.service';
import { ShareService } from 'src/app/services/share.service';
import { UserDataService } from 'src/app/services/user-data.service';
import { WeathermapTokenService } from 'src/app/services/weathermap-token.service';
import { SidePanelComponent } from 'src/app/side-panel/side-panel.component';
import { ToolbarComponent } from 'src/app/toolbar/toolbar.component';
import { NAProtocolService } from 'src/app/utils/na-protocol';
import { JawgToken, Layers, MAP_CENTER_CACHE_KEY, MAP_ZOOM_CACHE_KEY } from 'src/app/weathermap.const';
import { FriendsLayerComponent } from './layers/friends-layer/friends-layer.component';

@Component({
  selector: 'app-root',
  imports: [
    SidePanelComponent,
    SearchComponent,
    ControlsComponent,
    ToolbarComponent,
    HomesLayerComponent,
    FriendsLayerComponent,
    FavoriteLayerComponent,
    LegendComponent,
    BannerComponent,
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent implements OnInit, AfterViewInit {
  private readonly selectedStationService = inject(SelectedStationService);
  private readonly langsResolver = inject(LangsResolver);
  private readonly weathermapFacade = inject(WeathermapFacade);
  private readonly naProtocolService = inject(NAProtocolService);
  private readonly localStorageService = inject(LocalStorageService);
  private readonly layersService = inject(LayersService);
  private readonly loginService = inject(LoginService);
  private readonly userDataService = inject(UserDataService);
  private readonly weathermapTokenService = inject(WeathermapTokenService);
  private readonly pinService = inject(PinService);
  private readonly cacheService = inject(CacheService);
  private readonly shareService = inject(ShareService);
  private readonly eventManagerService = inject(EventManagerService);
  private readonly langPipe = inject(LangPipe);

  map: maplibregl.Map;

  ngOnInit(): void {
    this.langsResolver.resolve();

    this.weathermapTokenService.fetchToken();

    this.shareService.loadFromUrl();
  }

  async ngAfterViewInit(): Promise<void> {
    const { center, zoom, stationId } = this.cacheService.loadConfigFromCache();

    this.map = window.map = new maplibregl.Map({
      container: 'map',
      center,
      zoom,
      minZoom: 3,
      maxZoom: 14.9,
      attributionControl: false,
    });

    const maplibreAttribution = '<a href="https://maplibre.org">Maplibre</a>';

    const legalMentions = `<a href="https://legals.netatmo.com/?goto=legal-mentions&read_only=true">
      ${this.langPipe.transform('legals.__COMMON_LEGAL_MENTIONS')}</a>`;

    const termsOfUse = `<a href="https://legals.netatmo.com/?goto=terms&read_only=true">
      ${this.langPipe.transform('legals.__COMMON_TERMS_USE')}</a>`;

    const privacyPolicy = `<a href="https://legals.netatmo.com/?goto=privacy&read_only=true">
      ${this.langPipe.transform('legals.__COMMON_PRIVACY_POLICY')}</a>`;

    this.map.addControl(new maplibregl.NavigationControl(), 'top-left');
    this.map.addControl(new maplibregl.AttributionControl({
      compact: false,
      customAttribution: `${legalMentions} | ${termsOfUse} | ${privacyPolicy} | ${maplibreAttribution}`,
    }));

    this.map.addControl(
      new maplibregl.GeolocateControl({
        positionOptions: {
          enableHighAccuracy: true,
        },
        trackUserLocation: true,
      }),
      'top-left',
    );

    this.pinService.loadIcons();

    this.map.setStyle(`https://api.jawg.io/styles/7c47e08c-62b8-4523-a265-32267d4f8e9b.json?access-token=${JawgToken}`, {
      transformStyle: (_, nextStyle) => {
        return {
          ...nextStyle,
          transition: { delay: 0, duration: 0 },
        };
      },
    });

    // disable map rotation using right click + drag
    this.map.dragRotate.disable();
    // disable map rotation using keyboard
    this.map.keyboard.disable();
    // disable map rotation using touch rotation gesture
    this.map.touchZoomRotate.disableRotation();

    const zoom$ = new BehaviorSubject(this.map.getZoom().toFixed(0));

    this.map.on('zoomstart', () => {
      zoom$.next(this.map.getZoom().toFixed(0));
    });

    this.map.loadImage('/assets/selected-station.png').then((image) => this.map.addImage('selected-station', image.data));

    this.map.on('load', async () => {
      const pinType = this.weathermapFacade.selectedPintype();

      maplibregl.addProtocol('NAProtocol', this.naProtocolService.tile);
      this.map.addSource('stations', {
        type: 'vector',
        url: `NAProtocol://${pinType}`,
      });

      // wait for the token to be set before adding the layer
      this.weathermapTokenService.token$.pipe(
        isDefined(),
        take(1),
      ).subscribe(() => {
        this.map.addLayer(Layers[pinType]);
        this.cacheService.loadStation(stationId);
      });

      this.layersService.addHomeLayer();
      this.layersService.addFriendLayer();
      this.layersService.addFavoriteLayer();
      this.layersService.addSelectedLayer();

      this.map.on('click', 'stations', (e) => {
        this.selectedStationService.selectFeature(e.features[0]);
      });

      this.map.on('click', 'home-stations', (e) => {
        this.selectedStationService.selectFeature(e.features[0]);
      });

      this.map.on('click', 'friend-stations', (e) => {
        this.selectedStationService.selectFeature(e.features[0]);
      });

      this.map.on('click', 'favorite-stations', (e) => {
        this.selectedStationService.selectFeature(e.features[0]);
      });

      this.map.on('click', () => {
        this.eventManagerService.closeSources(EventSource.TOOLBAR, EventSource.SEARCH);
      });

      this.map.on('moveend', () => {
        this.localStorageService.setItem(MAP_CENTER_CACHE_KEY, JSON.stringify(this.map.getCenter()));
        this.localStorageService.setItem(MAP_ZOOM_CACHE_KEY, JSON.stringify(this.map.getZoom()));
      });
    });

    await this.loginService.assertToken();

    if (this.loginService.loggedIn()) {
      await this.userDataService.load();
    }
  }
}
